import { LOGGED_OUT } from 'actions/types';
const initialState = {
    potActivities: {},
    potPendingActivities: {},
    requestingPotActivities: {},
    potActivitiesPaginationToken: {},
    isErrorInFetchingPotsAcc: undefined,
    autoSaveActivities: undefined,
    editingUpcomingPayment: undefined,
    upcomingAutosavePayments: undefined,
    fetchingAutosaveActivities: undefined,
    autosaveActivitiesPaginationToken: undefined,
    fetchingPotsAccount: undefined,
};
const unpersistedPotsReducer = (state = initialState, action) => {
    switch (action.type) {
        case LOGGED_OUT: {
            return initialState;
        }
        case 'GET_POT_ACTIVITIES_REQUEST': {
            if (action.extra.params.status === 'PENDING') {
                return state;
            }
            const { potId } = action.extra;
            const { paging } = action.extra.params;
            const currentPaginationToken = state.potActivitiesPaginationToken?.[potId];
            return {
                ...state,
                requestingPotActivities: {
                    ...state.requestingPotActivities,
                    [potId]: true,
                },
                potActivitiesPaginationToken: {
                    ...state.potActivitiesPaginationToken,
                    [potId]: 'page' in paging && paging.page === 1 ? undefined : currentPaginationToken,
                },
            };
        }
        case 'GET_POT_ACTIVITIES_FAILURE': {
            if (action.extra.params.status === 'PENDING') {
                return state;
            }
            const requestingPotActivities = { ...state.requestingPotActivities };
            delete requestingPotActivities[action.extra.potId];
            return {
                ...state,
                requestingPotActivities,
            };
        }
        case 'GET_POT_ACTIVITIES_SUCCESS': {
            const { potId } = action.extra;
            const initialDepositId = state.pendingInitialDeposit?.id;
            let { pendingInitialDeposit } = state;
            if (action.payload.activities.some((activity) => activity.id !== initialDepositId)) {
                // Any other activity means the deposit is no longer the user's only one
                pendingInitialDeposit = undefined;
            }
            else {
                const updatedInitialDeposit = action.payload.activities.find((activity) => activity.type === 'DEPOSIT' && activity.id === initialDepositId);
                if (updatedInitialDeposit) {
                    pendingInitialDeposit = updatedInitialDeposit.status === 'PENDING' ? updatedInitialDeposit : undefined;
                }
            }
            if (action.extra.params.status === 'PENDING') {
                return {
                    ...state,
                    pendingInitialDeposit,
                    potPendingActivities: {
                        ...state.potPendingActivities,
                        [potId]: action.payload.activities,
                    },
                };
            }
            const currentPaginationToken = state.potActivitiesPaginationToken?.[potId];
            if (currentPaginationToken !==
                ('token' in action.extra.params.paging ? action.extra.params.paging.token : undefined)) {
                return state;
            }
            const requestingPotActivities = { ...state.requestingPotActivities };
            delete requestingPotActivities[potId];
            const currentActivities = state.potActivities?.[potId] || [];
            return {
                ...state,
                pendingInitialDeposit,
                potActivities: {
                    ...state.potActivities,
                    [potId]: !currentPaginationToken
                        ? action.payload.activities
                        : [...currentActivities, ...action.payload.activities],
                },
                requestingPotActivities,
                potActivitiesPaginationToken: {
                    ...state.potActivitiesPaginationToken,
                    [potId]: action.payload.activities.length < 30 ? null : action.payload.paging.next || null,
                },
            };
        }
        case 'GET_ALL_POTS_ACTIVITIES_SUCCESS': {
            const { activities } = action.payload;
            const [activity] = activities;
            return {
                ...state,
                pendingInitialDeposit: activities.length === 1 && activity.type === 'DEPOSIT' && activity.status === 'PENDING'
                    ? activity
                    : undefined,
            };
        }
        case 'GET_SAVINGS_PLAN_SUCCESS': {
            return {
                ...state,
                upcomingAutosavePayments: action.payload.upcomingPayments,
            };
        }
        case 'GET_POTS_ACCOUNT_SUCCESS': {
            return {
                ...state,
                fetchingPotsAccount: false,
                isErrorInFetchingPotsAcc: false,
                // Activities are no longer fetched once a pot is funded, so drop any stale pending deposit
                pendingInitialDeposit: action.payload?.hasFundedPot ? undefined : state.pendingInitialDeposit,
            };
        }
        case 'GET_POTS_ACCOUNT_REQUEST': {
            return {
                ...state,
                fetchingPotsAccount: true,
            };
        }
        case 'GET_POTS_ACCOUNT_FAILURE': {
            return {
                ...state,
                fetchingPotsAccount: false,
                isErrorInFetchingPotsAcc: true,
            };
        }
        case 'GET_AUTOSAVE_ACTIVITIES_REQUEST': {
            return {
                ...state,
                fetchingAutosaveActivities: true,
                autosaveActivitiesPaginationToken: 'page' in action.extra && action.extra.page === 1 ? undefined : state.autosaveActivitiesPaginationToken,
            };
        }
        case 'GET_AUTOSAVE_ACTIVITIES_SUCCESS': {
            if (!state.autosaveActivitiesPaginationToken) {
                return {
                    ...state,
                    fetchingAutosaveActivities: false,
                    autoSaveActivities: action.payload.activities,
                    upcomingAutosavePayments: action.payload.upcomingPayments,
                    autosaveActivitiesPaginationToken: action.payload.activities.length < 30 ? null : action.payload.paging.next,
                };
            }
            if ('token' in action.extra && state.autosaveActivitiesPaginationToken === action.extra.token) {
                return {
                    ...state,
                    fetchingAutosaveActivities: false,
                    autosaveActivitiesPaginationToken: action.payload.paging?.next || null,
                    upcomingAutosavePayments: action.payload.upcomingPayments,
                    autoSaveActivities: [...(state.autoSaveActivities ?? []), ...action.payload.activities],
                };
            }
            return state;
        }
        case 'GET_AUTOSAVE_ACTIVITIES_FAILURE': {
            return {
                ...state,
                fetchingAutosaveActivities: false,
            };
        }
        case 'EDIT_AUTOSAVE_UPCOMING_PAYMENT_REQUEST': {
            return {
                ...state,
                editingUpcomingPayment: true,
            };
        }
        case 'EDIT_AUTOSAVE_UPCOMING_PAYMENT_SUCCESS': {
            return {
                ...state,
                editingUpcomingPayment: false,
                upcomingAutosavePayments: [action.payload.upcomingPayment],
            };
        }
        case 'EDIT_AUTOSAVE_UPCOMING_PAYMENT_FAILURE': {
            return {
                ...state,
                editingUpcomingPayment: false,
            };
        }
        case 'EDIT_SAVINGS_PLAN_SUCCESS': {
            return {
                ...state,
                upcomingAutosavePayments: action.payload.upcomingPayments,
            };
        }
        default: {
            return state;
        }
    }
};
export default unpersistedPotsReducer;
